import React, { useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { MONTHS } from "../constants";
import ShopLocationsMap from "../charts/ShopLocationsMap";
import { useAllShopsData } from "../../services/API/ShopAPI/GetAllShopDetails.jsx";

/**
 * Checks whether a date value is valid.
 *
 * @param {Date} date - Date object to validate.
 * @returns {boolean} True when the date is valid.
 */
const isValidDate = (date) => {
  return !Number.isNaN(date.getTime());
};

/**
 * Returns the shop name or a fallback label.
 *
 * @param {object} shop - Shop object returned by the API.
 * @returns {string} Shop display name.
 */
const getShopName = (shop) => {
  return shop?.name || shop?.shop_name || "Unnamed shop";
};

/**
 * Creates the shop trends chart.
 *
 * The chart can display:
 * - Monthly shop creation totals.
 * - Daily shop creation totals when a year and month are selected.
 *
 * @param {object[]} shopList - Shops returned by the dashboard API.
 */
const ShopTrendsChart = ({ shopList = [] }) => {

  // Loads the complete shop data including location information.
const {
  data: allShopsData = [],
} = useAllShopsData();

  /**
   * Stores the selected chart period index.
   *
   * This index is used to open the shop details popup.
   */
  const [selectedPeriodIndex, setSelectedPeriodIndex] = useState(null);

  /**
   * Stores the selected year filter.
   */
  const [selectedYear, setSelectedYear] = useState("");

  /**
   * Stores the selected month filter.
   */
  const [selectedMonth, setSelectedMonth] = useState("");

  /**
   * Returns all years that contain valid shop creation dates.
   *
   * Duplicate years are removed and the newest year appears first.
   */
  const availableYears = useMemo(() => {
    const years = shopList
      .map((shop) => {
        const createdAt = new Date(shop?.date_created);

        return isValidDate(createdAt) ? createdAt.getFullYear() : null;
      })
      .filter((year) => year !== null);

    return [...new Set(years)].sort(
      (firstYear, secondYear) => secondYear - firstYear,
    );
  }, [shopList]);

  /**
   * Returns the months that contain shop records for the selected year.
   *
   * This value can be used to disable month options that contain no data.
   */
  const availableMonths = useMemo(() => {
    if (!selectedYear) {
      return [];
    }

    const existingMonths = shopList
      .filter((shop) => {
        const createdAt = new Date(shop?.date_created);

        return (
          isValidDate(createdAt) &&
          String(createdAt.getFullYear()) === selectedYear
        );
      })
      .map((shop) => {
        const createdAt = new Date(shop.date_created);

        return String(createdAt.getMonth() + 1).padStart(2, "0");
      });

    return [...new Set(existingMonths)].sort();
  }, [shopList, selectedYear]);

  /**
   * Filters shops according to the selected year and month.
   */
  const filteredShopList = useMemo(() => {
    return shopList.filter((shop) => {
      const createdAt = new Date(shop?.date_created);

      if (!isValidDate(createdAt)) {
        return false;
      }

      const year = String(createdAt.getFullYear());

      const month = String(createdAt.getMonth() + 1).padStart(2, "0");

      const matchesYear = selectedYear ? year === selectedYear : true;

      const matchesMonth = selectedMonth ? month === selectedMonth : true;

      return matchesYear && matchesMonth;
    });
  }, [shopList, selectedYear, selectedMonth]);

  /**
   * Creates the categories, chart values and shop names.
   *
   * When both year and month are selected, shops are grouped by day.
   * Otherwise, shops are grouped by month.
   */
  const { categories, seriesData, shopNamesByPeriod } = useMemo(() => {
    /**
     * Creates a daily view when a specific year and month are selected.
     */
    if (selectedYear && selectedMonth) {
      const dailyData = {};

      filteredShopList.forEach((shop) => {
        const createdAt = new Date(shop?.date_created);

        if (!isValidDate(createdAt)) {
          return;
        }

        const day = createdAt.getDate();

        /**
         * Creates the day group when it does not exist.
         */
        if (!dailyData[day]) {
          dailyData[day] = {
            count: 0,
            names: [],
          };
        }

        /**
         * Increases the number of shops created on this day.
         */
        dailyData[day].count += 1;

        /**
         * Stores the shop name for the tooltip and details popup.
         */
        dailyData[day].names.push(getShopName(shop));
      });

      /**
       * Sorts the days in ascending order.
       */
      const sortedDays = Object.keys(dailyData)
        .map(Number)
        .sort((firstDay, secondDay) => firstDay - secondDay);

      return {
        categories: sortedDays.map((day) => String(day)),

        seriesData: sortedDays.map((day) => dailyData[day].count),

        shopNamesByPeriod: sortedDays.map((day) => dailyData[day].names),
      };
    }

    /**
     * Creates a monthly view when no specific month is selected.
     */
    const monthlyData = {};

    filteredShopList.forEach((shop) => {
      const createdAt = new Date(shop?.date_created);

      if (!isValidDate(createdAt)) {
        return;
      }

      const monthKey = `${createdAt.getFullYear()}-${String(
        createdAt.getMonth() + 1,
      ).padStart(2, "0")}`;

      /**
       * Creates the month group when it does not exist.
       */
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {
          count: 0,
          names: [],
        };
      }

      /**
       * Increases the number of shops created during this month.
       */
      monthlyData[monthKey].count += 1;

      /**
       * Stores the shop name for the tooltip and details popup.
       */
      monthlyData[monthKey].names.push(getShopName(shop));
    });

    /**
     * Sorts months chronologically.
     */
    const sortedMonths = Object.keys(monthlyData).sort();

    return {
      categories: sortedMonths.map((monthKey) => {
        const [year, month] = monthKey.split("-");

        const date = new Date(Number(year), Number(month) - 1);

        return date.toLocaleString("en-GB", {
          month: "short",
          year: "numeric",
        });
      }),

      seriesData: sortedMonths.map((monthKey) => monthlyData[monthKey].count),

      shopNamesByPeriod: sortedMonths.map(
        (monthKey) => monthlyData[monthKey].names,
      ),
    };
  }, [filteredShopList, selectedYear, selectedMonth]);

  /**
   * Calculates the total number of shops displayed in the chart.
   */
  const totalShops = useMemo(() => {
    return seriesData.reduce((total, value) => total + value, 0);
  }, [seriesData]);

  /**
   * Returns the subtitle according to the selected filters.
   */
  const chartSubtitle = useMemo(() => {
    if (selectedYear && selectedMonth) {
      const selectedMonthLabel =
        MONTHS.find((month) => month.value === selectedMonth)?.label ||
        selectedMonth;

      return `${totalShops} total shops — ${selectedMonthLabel} ${selectedYear}`;
    }

    if (selectedYear) {
      return `${totalShops} total shops — ${selectedYear}`;
    }

    return `${totalShops} total shops — monthly breakdown`;
  }, [selectedYear, selectedMonth, totalShops]);

  /**
   * Configures the ApexCharts area chart.
   */
  const chartOptions = useMemo(
    () => ({
      chart: {
        id: "shop-trends",
        type: "area",
        height: 360,
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: false,
        },
        animations: {
          enabled: true,
          easing: "easeinout",
          speed: 600,
        },
        fontFamily:
          'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial',
      },

      /**
       * Displays the value above each chart point.
       */
      dataLabels: {
        enabled: true,
        offsetY: -10,
        style: {
          fontSize: "12px",
          fontWeight: "bold",
          colors: ["#222731"],
        },
        background: {
          enabled: false,
        },
      },

      /**
       * Chart title.
       */
      title: {
        text: "Shop Creation Trends",
        align: "center",
        style: {
          fontSize: "16px",
          fontWeight: 700,
          color: "#44403c",
        },
      },

      /**
       * Chart subtitle containing the current total.
       */
      subtitle: {
        text: chartSubtitle,
        align: "center",
        style: {
          color: "#78716c",
          fontSize: "12px",
        },
      },

      /**
       * Defines the chart line appearance.
       */
      stroke: {
        curve: "smooth",
        width: 2,
      },

      /**
       * Defines the chart point appearance.
       */
      markers: {
        size: 6,
        hover: {
          size: 8,
        },
        strokeWidth: 2,
        radius: 3,
        colors: ["#fafaf9"],
        strokeColors: ["#222731"],
      },

      /**
       * Defines the chart grid appearance.
       */
      grid: {
        borderColor: "#e7e5e4",
        strokeDashArray: 4,
      },

      /**
       * Displays shop totals and shop names when hovering over a point.
       */
      tooltip: {
        enabled: true,
        shared: false,
        intersect: false,

        custom: ({ series, seriesIndex, dataPointIndex, w }) => {
          const periodLabel =
            selectedYear && selectedMonth
              ? `Day ${w.globals.labels[dataPointIndex]}`
              : w.globals.labels[dataPointIndex] || "";

          const count = series[seriesIndex][dataPointIndex] ?? 0;

          const names = shopNamesByPeriod[dataPointIndex] ?? [];

          const shopListHtml = names
            .map((name) => `<li style="margin-bottom:4px;">${name}</li>`)
            .join("");

          return `
            <div
              style="
                padding: 12px;
                max-width: 260px;
                border-radius: 8px;
                font-family: Inter, sans-serif;
              "
            >
              <div
                style="
                  margin-bottom: 6px;
                  color: #44403c;
                  font-weight: 700;
                "
              >
                ${periodLabel}
              </div>

              <div
                style="
                  margin-bottom: 8px;
                  color: #78716c;
                  font-size: 13px;
                "
              >
                <strong>
                  ${count}
                  ${count === 1 ? "shop" : "shops"}
                </strong>
              </div>

              <ul
                style="
                  margin: 0;
                  padding-left: 18px;
                  color: #57534e;
                  font-size: 13px;
                "
              >
                ${shopListHtml}
              </ul>
            </div>
          `;
        },
      },

      /**
       * Configures the horizontal chart axis.
       */
      xaxis: {
        categories,
        labels: {
          rotate: -45,
          style: {
            colors: "#78716c",
            fontSize: "12px",
          },
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
      },

      /**
       * Configures the vertical chart axis.
       */
      yaxis: {
        title: {
          text: "Shops",
          style: {
            color: "#a8a29e",
            fontSize: "16px",
            fontWeight: 500,
          },
        },
        labels: {
          style: {
            colors: "#78716c",
          },
          formatter: (value) => Math.floor(value),
        },
        min: 0,
        forceNiceScale: true,
      },

      /**
       * Defines the area gradient below the chart line.
       */
      fill: {
        type: "gradient",
        gradient: {
          shade: "light",
          type: "vertical",
          shadeIntensity: 0.5,
          gradientToColors: ["#48CFCB"],
          inverseColors: false,
          opacityFrom: 0.5,
          opacityTo: 0.06,
          stops: [0, 80, 100],
        },
      },

      /**
       * Defines the primary chart color.
       */
      colors: ["#222731"],

      /**
       * Prevents decimal values from appearing on the vertical axis.
       */
      noData: {
        text: "No shop data available",
        align: "center",
        verticalAlign: "middle",
        style: {
          color: "#a8a29e",
          fontSize: "14px",
        },
      },
    }),
    [categories, chartSubtitle, selectedYear, selectedMonth, shopNamesByPeriod],
  );

  /**
   * Creates the ApexCharts data series.
   */
  const series = useMemo(
    () => [
      {
        name: "Shops Created",
        data: seriesData,
      },
    ],
    [seriesData],
  );

  /**
   * Handles changes to the year filter.
   *
   * Changing the year resets the selected month and popup.
   */
  const handleYearChange = (event) => {
    setSelectedYear(event.target.value);
    setSelectedMonth("");
    setSelectedPeriodIndex(null);
  };

  /**
   * Handles changes to the month filter.
   */
  const handleMonthChange = (event) => {
    setSelectedMonth(event.target.value);
    setSelectedPeriodIndex(null);
  };

  /**
   * Opens the details popup for a selected chart period.
   */
  const handlePeriodClick = (index) => {
    setSelectedPeriodIndex(index);
  };

  /**
   * Closes the period details popup.
   */
  const closePeriodDetails = () => {
    setSelectedPeriodIndex(null);
  };

  return (
    <section className="h-full rounded-2xl border border-stone-100 bg-stone-50 p-4 shadow-sm">
      {/* Chart header and filters */}
      <header className="mb-3 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h3 className="text-lg font-semibold text-stone-800">
            Shop Creation Trends
          </h3>

          <p className="text-sm text-stone-500">{chartSubtitle}</p>
        </div>

        {/* Year and month filters */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <select
            value={selectedYear}
            onChange={handleYearChange}
            aria-label="Filter shops by year"
            className="rounded-md border border-stone-200 bg-stone-50 px-3 py-1.5 text-sm text-stone-700 shadow-sm outline-none focus:ring-2 focus:ring-stone-300"
          >
            <option value="">Select Year</option>

            {availableYears.map((year) => (
              <option key={year} value={String(year)}>
                {year}
              </option>
            ))}
          </select>

          <select
            value={selectedMonth}
            disabled={!selectedYear}
            onChange={handleMonthChange}
            aria-label="Filter shops by month"
            className={`rounded-md border px-3 py-1.5 text-sm shadow-sm outline-none focus:ring-2 focus:ring-stone-300 ${
              !selectedYear
                ? "cursor-not-allowed border-stone-200 bg-stone-100 text-stone-400"
                : "border-stone-200 bg-stone-50 text-stone-700"
            }`}
          >
            {MONTHS.map((month) => {
              const isMonthUnavailable =
                month.value &&
                selectedYear &&
                !availableMonths.includes(month.value);

              return (
                <option
                  key={month.value || "all"}
                  value={month.value}
                  disabled={isMonthUnavailable}
                >
                  {month.label}
                </option>
              );
            })}
          </select>

          {/* Opens the average by period popup */}
          <ShopLocationsMap shopList={allShopsData} />
        </div>
      </header>

      {/* Chart container */}
      <div id="shop-trends-chart" className="rounded-xl bg-stone-50 p-3">
        {seriesData.length === 0 ? (
          <div className="flex h-56 items-center justify-center">
            <p className="text-stone-400">No shop data available</p>
          </div>
        ) : (
          <ReactApexChart
            options={chartOptions}
            series={series}
            type="area"
            height={360}
          />
        )}
      </div>

      {/* Period buttons */}
      {selectedYear && categories.length > 0 && (
        <div className="mt-4">
          <div className="flex flex-wrap gap-2">
            {categories.map((label, index) => (
              <button
                key={`${label}-${index}`}
                type="button"
                onClick={() => handlePeriodClick(index)}
                aria-pressed={selectedPeriodIndex === index}
                className="flex items-center gap-2 rounded-full border border-stone-200 bg-stone-50 px-3 py-1.5 text-sm shadow-sm transition hover:shadow-md"
              >
                <span className="font-medium text-stone-700">{label}</span>

                <span className="text-xs text-stone-500">
                  {seriesData[index]}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Selected period details popup */}
      {selectedPeriodIndex !== null && (
        <div className="fixed inset-0 z-50 flex items-end justify-center p-4 md:items-center md:p-6">
          {/* Popup background overlay */}
          <button
            type="button"
            aria-label="Close period details"
            onClick={closePeriodDetails}
            className="absolute inset-0 cursor-default bg-black/30"
          />

          {/* Popup content */}
          <div className="relative w-full max-w-lg overflow-hidden rounded-xl border border-stone-100 bg-white shadow-xl">
            {/* Popup header */}
            <header className="flex items-center justify-between border-b border-stone-100 p-4">
              <div>
                <h4 className="text-sm font-semibold text-stone-800">
                  {categories[selectedPeriodIndex]}
                </h4>

                <p className="text-xs text-stone-500">
                  {seriesData[selectedPeriodIndex]}{" "}
                  {seriesData[selectedPeriodIndex] === 1 ? "shop" : "shops"}
                </p>
              </div>

              <button
                type="button"
                onClick={closePeriodDetails}
                aria-label="Close details"
                className="rounded-md px-2 py-1 text-stone-400 hover:bg-stone-100 hover:text-stone-600"
              >
                ✕
              </button>
            </header>

            {/* Shop list without internal scrolling */}
            <div className="p-4">
              {(shopNamesByPeriod[selectedPeriodIndex] ?? []).length === 0 ? (
                <p className="text-sm text-stone-400">
                  No shops found for this period.
                </p>
              ) : (
                <ul className="space-y-2">
                  {(shopNamesByPeriod[selectedPeriodIndex] ?? []).map(
                    (name, index) => (
                      <li
                        key={`${name}-${index}`}
                        className="rounded-md border border-stone-100 bg-stone-50 p-2 text-sm text-stone-700"
                      >
                        {name}
                      </li>
                    ),
                  )}
                </ul>
              )}
            </div>

            {/* Popup footer */}
            <footer className="flex justify-end border-t border-stone-100 p-3">
              <button
                type="button"
                onClick={closePeriodDetails}
                className="rounded-md border border-stone-200 bg-stone-50 px-3 py-1.5 text-sm text-stone-700 hover:bg-stone-100"
              >
                Close
              </button>
            </footer>
          </div>
        </div>
      )}
    </section>
  );
};

export default ShopTrendsChart;
